import {
    ActionConfig,
    AlertRuleActionType,
    WebhookAuthorizationForm,
    WebhookAuthorizationPayload,
    WebhookAuthorizationType,
    WebhookHeader,
} from '@/types/alert';

export const VALIDATION_LIMITS = {
    MIN_PROMPT_LENGTH: 3,
    MIN_CONFIDENCE_THRESHOLD: 0,
    MAX_CONFIDENCE_THRESHOLD: 100,
} as const;

export type ValidationLimits = typeof VALIDATION_LIMITS;

const AUTHORIZATION_HEADER_NAME = 'authorization';
const RESERVED_HEADER_NAMES = [
    'host',
    'content-length',
    'x-conntour-request-id',
];

export type HeaderNameError = 'authorization' | 'reserved' | 'duplicate';

export function headerNameError(
    header: WebhookHeader,
    headers: WebhookHeader[],
): HeaderNameError | null {
    const name = header.name.trim().toLowerCase();
    if (name === '') return null;
    if (name === AUTHORIZATION_HEADER_NAME) return 'authorization';
    if (RESERVED_HEADER_NAMES.includes(name)) return 'reserved';
    const isDuplicate = headers.some(
        (other) =>
            other.id !== header.id && other.name.trim().toLowerCase() === name,
    );
    return isDuplicate ? 'duplicate' : null;
}

export function emptyAuthorization(): WebhookAuthorizationForm {
    return { type: null, username: '', secret: '', stored: null };
}

export function isDeliverableWebhookUrl(url: string): boolean {
    try {
        const parsed = new URL(url);
        return (
            ['http:', 'https:'].includes(parsed.protocol) &&
            parsed.hostname !== '' &&
            parsed.port !== '0' &&
            parsed.username === '' &&
            parsed.password === ''
        );
    } catch {
        return false;
    }
}

export function webhookUrlOrigin(url: string): string | null {
    try {
        return new URL(url).origin;
    } catch {
        return null;
    }
}

export function nonEmptyHeaders(
    headers: WebhookHeader[],
): Array<{ name: string; value: string }> {
    return headers
        .map((header) => ({
            name: header.name.trim(),
            value: header.value.trim(),
        }))
        .filter((header) => header.name !== '' && header.value !== '');
}

export function authorizationPayload(
    authorization: WebhookAuthorizationForm,
): WebhookAuthorizationPayload | null {
    if (authorization.type === null) return null;
    const secret = authorization.secret.trim();
    return {
        type: authorization.type,
        ...(authorization.type === WebhookAuthorizationType.BASIC && {
            username: authorization.username.trim(),
        }),
        ...(secret !== '' && { secret }),
    };
}

export function isSecretStored(
    authorization: WebhookAuthorizationForm,
    url: string,
): boolean {
    return (
        authorization.stored !== null &&
        authorization.stored.type === authorization.type &&
        authorization.stored.origin === webhookUrlOrigin(url)
    );
}

export function isStoredSecretLeftBehind(
    authorization: WebhookAuthorizationForm,
    url: string,
): boolean {
    return (
        authorization.stored !== null &&
        authorization.stored.type === authorization.type &&
        !isSecretStored(authorization, url)
    );
}

export function isAuthorizationComplete(
    authorization: WebhookAuthorizationForm,
    url: string,
): boolean {
    if (authorization.type === null) return true;
    const hasSecret =
        authorization.secret.trim() !== '' ||
        isSecretStored(authorization, url);
    if (authorization.type === WebhookAuthorizationType.BASIC) {
        return hasSecret && authorization.username.trim() !== '';
    }
    return hasSecret;
}

export function isActionConfigured(action: ActionConfig): boolean {
    if (action.type !== AlertRuleActionType.WEBHOOK) return true;
    if (!action.webhook) return false;
    const { url, headers, authorization } = action.webhook;
    return (
        isDeliverableWebhookUrl(url) &&
        isAuthorizationComplete(authorization, url) &&
        headers.every((header) => headerNameError(header, headers) === null)
    );
}
